/**
 * Visual Style Guide Page
 * 
 * Showcases all design system components, colors, typography, and patterns
 * for the GoodwinCares/HOS application.
 */

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  AlertTriangle, CheckCircle, Info, AlertCircle, 
  Sun, Moon, Contrast, Palette, Type, Layout,
  Square, Copy, Check, ArrowLeft
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@/contexts/ThemeContext";

// Color palette configuration
const COLOR_PALETTE = {
  primary: [
    { name: "teal-50", value: "#F0FDFA", textDark: true },
    { name: "teal-100", value: "#CCFBF1", textDark: true },
    { name: "teal-200", value: "#99F6E4", textDark: true },
    { name: "teal-300", value: "#5EEAD4", textDark: true },
    { name: "teal-400", value: "#2DD4BF", textDark: true },
    { name: "teal-500", value: "#14B8A6", textDark: false },
    { name: "teal-600", value: "#0D9488", textDark: false },
    { name: "teal-700", value: "#0F766E", textDark: false },
    { name: "teal-800", value: "#115E59", textDark: false },
    { name: "teal-900", value: "#134E4A", textDark: false },
    { name: "teal-950", value: "#042F2E", textDark: false },
  ],
  neutral: [
    { name: "slate-50", value: "#F8FAFC", textDark: true },
    { name: "slate-100", value: "#F1F5F9", textDark: true },
    { name: "slate-200", value: "#E2E8F0", textDark: true },
    { name: "slate-300", value: "#CBD5E1", textDark: true },
    { name: "slate-400", value: "#94A3B8", textDark: true },
    { name: "slate-500", value: "#64748B", textDark: false },
    { name: "slate-600", value: "#475569", textDark: false },
    { name: "slate-700", value: "#334155", textDark: false },
    { name: "slate-800", value: "#1E293B", textDark: false },
    { name: "slate-900", value: "#0F172A", textDark: false },
    { name: "slate-950", value: "#020617", textDark: false },
  ],
  status: [
    { name: "Critical", value: "#DC2626", bg: "bg-red-600" },
    { name: "Warning", value: "#D97706", bg: "bg-amber-600" },
    { name: "Success", value: "#059669", bg: "bg-emerald-600" },
    { name: "Info", value: "#2563EB", bg: "bg-blue-600" },
  ]
};

function ColorSwatch({ name, value, textDark, onCopy }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    toast.success(`Copied ${value}`);
    setTimeout(() => setCopied(false), 2000);
    if (onCopy) onCopy(value);
  };

  return (
    <button
      onClick={handleCopy}
      className="group relative flex flex-col items-center transition-transform hover:scale-105"
      data-testid={`color-swatch-${name}`}
    >
      <div 
        className="w-16 h-16 rounded-lg shadow-md border border-slate-200 dark:border-slate-700 flex items-center justify-center"
        style={{ backgroundColor: value }}
      >
        {copied ? (
          <Check className={`w-5 h-5 ${textDark ? "text-slate-800" : "text-white"}`} />
        ) : (
          <Copy className={`w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity ${textDark ? "text-slate-800" : "text-white"}`} />
        )}
      </div>
      <span className="text-xs mt-1.5 text-slate-600 dark:text-slate-400">{name}</span>
      <span className="text-[10px] text-slate-400 dark:text-slate-500 font-mono">{value}</span>
    </button>
  );
}

export default function StyleGuidePage() {
  const navigate = useNavigate();
  const { theme, setTheme, highContrast, setHighContrast } = useTheme();
  const [activeTab, setActiveTab] = useState("colors");

  return (
    <div className={`min-h-screen transition-colors ${theme === "dark" ? "bg-slate-950 text-white" : "bg-slate-50 text-slate-900"}`} data-testid="style-guide-page">
      {/* Header */}
      <div className={`sticky top-0 z-40 backdrop-blur border-b ${theme === "dark" ? "bg-slate-900/95 border-slate-800" : "bg-white/95 border-slate-200"}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate(-1)}
              className={theme === "dark" ? "text-slate-400 hover:text-white" : "text-slate-600 hover:text-slate-900"}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <Palette className="w-5 h-5 text-teal-500" />
                E1 Clinical Design System
              </h1>
              <p className={`text-sm ${theme === "dark" ? "text-slate-400" : "text-slate-600"}`}>
                GoodwinCares Visual Style Guide
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            {/* High Contrast Toggle */}
            <div className="flex items-center gap-2">
              <Contrast className="w-4 h-4 text-slate-500" />
              <Label className="text-sm">High Contrast</Label>
              <Switch 
                checked={highContrast} 
                onCheckedChange={setHighContrast}
                data-testid="high-contrast-toggle"
              />
            </div>
            
            {/* Theme Toggle */}
            <div className="flex items-center gap-2 border-l border-slate-700 pl-4">
              <Button
                variant={theme === "light" ? "default" : "ghost"}
                size="sm"
                onClick={() => setTheme("light")}
                className={theme === "light" ? "bg-teal-600" : ""}
              >
                <Sun className="w-4 h-4 mr-1" />
                Light
              </Button>
              <Button
                variant={theme === "dark" ? "default" : "ghost"}
                size="sm"
                onClick={() => setTheme("dark")}
                className={theme === "dark" ? "bg-teal-600" : ""}
              >
                <Moon className="w-4 h-4 mr-1" />
                Dark
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className={`grid w-full grid-cols-5 ${theme === "dark" ? "bg-slate-800" : "bg-slate-200"}`}>
            <TabsTrigger value="colors" className="flex items-center gap-1">
              <Palette className="w-4 h-4" />Colors
            </TabsTrigger>
            <TabsTrigger value="typography" className="flex items-center gap-1">
              <Type className="w-4 h-4" />Typography
            </TabsTrigger>
            <TabsTrigger value="components" className="flex items-center gap-1">
              <Square className="w-4 h-4" />Components
            </TabsTrigger>
            <TabsTrigger value="status" className="flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />Status
            </TabsTrigger>
            <TabsTrigger value="layout" className="flex items-center gap-1">
              <Layout className="w-4 h-4" />Layout
            </TabsTrigger>
          </TabsList>

          {/* Colors Tab */}
          <TabsContent value="colors" className="mt-6">
            <div className="space-y-8">
              {/* Primary Colors */}
              <Card className={theme === "dark" ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200"}>
                <CardHeader>
                  <CardTitle className="text-lg">Primary Palette - Clinical Teal</CardTitle>
                  <CardDescription>Core brand color for actions and highlights</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-4">
                    {COLOR_PALETTE.primary.map((color) => (
                      <ColorSwatch key={color.name} {...color} />
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Neutral Colors */}
              <Card className={theme === "dark" ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200"}>
                <CardHeader>
                  <CardTitle className="text-lg">Neutral Palette - Slate</CardTitle>
                  <CardDescription>Backgrounds, text, and borders</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-4">
                    {COLOR_PALETTE.neutral.map((color) => (
                      <ColorSwatch key={color.name} {...color} />
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Status Colors */}
              <Card className={theme === "dark" ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200"}>
                <CardHeader>
                  <CardTitle className="text-lg">Status Colors</CardTitle>
                  <CardDescription>Semantic colors for status indicators</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-4 gap-4">
                    {COLOR_PALETTE.status.map((status) => (
                      <div key={status.name} className="text-center">
                        <div className={`${status.bg} text-white px-4 py-3 rounded-lg font-semibold`}>
                          {status.name}
                        </div>
                        <span className="text-xs text-slate-500 font-mono mt-1 block">{status.value}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Typography Tab */}
          <TabsContent value="typography" className="mt-6">
            <div className="space-y-8">
              <Card className={theme === "dark" ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200"}>
                <CardHeader>
                  <CardTitle className="text-lg">Font Families</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <p className="text-sm text-slate-500 mb-2">Brand / Headings</p>
                    <p className="font-serif text-3xl">Spectral - Empathy & Warmth</p>
                    <code className={`text-xs ${theme === "dark" ? "text-slate-400" : "text-slate-600"}`}>font-family: &apos;Spectral&apos;, serif</code>
                  </div>
                  <Separator className={theme === "dark" ? "bg-slate-700" : "bg-slate-200"} />
                  <div>
                    <p className="text-sm text-slate-500 mb-2">UI / Body Text</p>
                    <p className="font-sans text-xl">Manrope - Clinical Legibility</p>
                    <code className={`text-xs ${theme === "dark" ? "text-slate-400" : "text-slate-600"}`}>font-family: &apos;Manrope&apos;, sans-serif</code>
                  </div>
                  <Separator className={theme === "dark" ? "bg-slate-700" : "bg-slate-200"} />
                  <div>
                    <p className="text-sm text-slate-500 mb-2">Code / Data</p>
                    <p className="font-mono text-lg">JetBrains Mono - Technical Data</p>
                    <code className={`text-xs ${theme === "dark" ? "text-slate-400" : "text-slate-600"}`}>font-family: &apos;JetBrains Mono&apos;, monospace</code>
                  </div>
                </CardContent>
              </Card>

              <Card className={theme === "dark" ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200"}>
                <CardHeader>
                  <CardTitle className="text-lg">Type Scale</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-baseline gap-4">
                    <span className="w-24 text-xs text-slate-500">5xl (48px)</span>
                    <h1 className="text-5xl font-bold">Heading 1</h1>
                  </div>
                  <div className="flex items-baseline gap-4">
                    <span className="w-24 text-xs text-slate-500">4xl (36px)</span>
                    <h2 className="text-4xl font-bold">Heading 2</h2>
                  </div>
                  <div className="flex items-baseline gap-4">
                    <span className="w-24 text-xs text-slate-500">3xl (30px)</span>
                    <h3 className="text-3xl font-semibold">Heading 3</h3>
                  </div>
                  <div className="flex items-baseline gap-4">
                    <span className="w-24 text-xs text-slate-500">2xl (24px)</span>
                    <h4 className="text-2xl font-semibold">Heading 4</h4>
                  </div>
                  <div className="flex items-baseline gap-4">
                    <span className="w-24 text-xs text-slate-500">xl (20px)</span>
                    <h5 className="text-xl font-medium">Heading 5</h5>
                  </div>
                  <div className="flex items-baseline gap-4">
                    <span className="w-24 text-xs text-slate-500">base (16px)</span>
                    <p className="text-base">Body text - regular paragraph content</p>
                  </div>
                  <div className="flex items-baseline gap-4">
                    <span className="w-24 text-xs text-slate-500">sm (14px)</span>
                    <p className="text-sm">Small text - secondary information</p>
                  </div>
                  <div className="flex items-baseline gap-4">
                    <span className="w-24 text-xs text-slate-500">xs (12px)</span>
                    <p className="text-xs">Extra small - labels and captions</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Components Tab */}
          <TabsContent value="components" className="mt-6">
            <div className="grid grid-cols-2 gap-6">
              {/* Buttons */}
              <Card className={theme === "dark" ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200"}>
                <CardHeader>
                  <CardTitle className="text-lg">Buttons</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex flex-wrap gap-3">
                    <Button className="bg-teal-600 hover:bg-teal-700">Primary</Button>
                    <Button variant="secondary">Secondary</Button>
                    <Button variant="outline">Outline</Button>
                    <Button variant="ghost">Ghost</Button>
                    <Button variant="destructive">Destructive</Button>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <Button size="sm" className="bg-teal-600">Small</Button>
                    <Button size="default" className="bg-teal-600">Default</Button>
                    <Button size="lg" className="bg-teal-600">Large</Button>
                  </div>
                </CardContent>
              </Card>

              {/* Inputs */}
              <Card className={theme === "dark" ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200"}>
                <CardHeader>
                  <CardTitle className="text-lg">Form Inputs</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Text Input</Label>
                    <Input placeholder="Enter value..." className={theme === "dark" ? "bg-slate-800 border-slate-700" : ""} />
                  </div>
                  <div className="space-y-2">
                    <Label>Textarea</Label>
                    <Textarea placeholder="Enter details..." rows={2} className={theme === "dark" ? "bg-slate-800 border-slate-700" : ""} />
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch />
                    <Label>Toggle Switch</Label>
                  </div>
                </CardContent>
              </Card>

              {/* Badges */}
              <Card className={theme === "dark" ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200"}>
                <CardHeader>
                  <CardTitle className="text-lg">Badges</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex flex-wrap gap-2">
                    <Badge className="bg-teal-600">Primary</Badge>
                    <Badge variant="secondary">Secondary</Badge>
                    <Badge variant="outline">Outline</Badge>
                    <Badge variant="destructive">Destructive</Badge>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Badge className="bg-red-600">Critical</Badge>
                    <Badge className="bg-amber-600">Warning</Badge>
                    <Badge className="bg-emerald-600">Success</Badge>
                    <Badge className="bg-blue-600">Info</Badge>
                  </div>
                </CardContent>
              </Card>

              {/* Cards */}
              <Card className={theme === "dark" ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200"}>
                <CardHeader>
                  <CardTitle className="text-lg">Cards</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className={`p-4 rounded-xl border ${theme === "dark" ? "bg-slate-800/50 border-slate-700" : "bg-white border-slate-200 shadow-sm"}`}>
                    <p className="font-medium mb-1">Standard Card</p>
                    <p className={`text-sm ${theme === "dark" ? "text-slate-400" : "text-slate-600"}`}>With subtle shadow</p>
                  </div>
                  <div className="p-4 rounded-xl bg-gradient-to-br from-teal-500/10 to-cyan-500/10 border border-teal-500/30">
                    <p className="font-medium mb-1 text-teal-400">Highlighted Card</p>
                    <p className="text-sm text-slate-400">Gradient accent</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Status Tab */}
          <TabsContent value="status" className="mt-6">
            <div className="space-y-6">
              <Card className={theme === "dark" ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200"}>
                <CardHeader>
                  <CardTitle className="text-lg">Status Indicators</CardTitle>
                  <CardDescription>Semantic status badges and alerts</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Status Badges */}
                  <div className="space-y-2">
                    <Label className="text-sm text-slate-500">Status Badges</Label>
                    <div className="flex flex-wrap gap-2">
                      <Badge className="bg-red-500/20 text-red-400 border border-red-500/30">Critical</Badge>
                      <Badge className="bg-amber-500/20 text-amber-400 border border-amber-500/30">Warning</Badge>
                      <Badge className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">Success</Badge>
                      <Badge className="bg-blue-500/20 text-blue-400 border border-blue-500/30">Info</Badge>
                    </div>
                  </div>

                  {/* Alert Boxes */}
                  <div className="space-y-3">
                    <Label className="text-sm text-slate-500">Alert Boxes</Label>
                    <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-red-400" />
                      <span className="text-sm text-red-300">Critical alert - Immediate action required</span>
                    </div>
                    <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-amber-400" />
                      <span className="text-sm text-amber-300">Warning alert - Review needed</span>
                    </div>
                    <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-400" />
                      <span className="text-sm text-emerald-300">Success alert - Action completed</span>
                    </div>
                    <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/30 flex items-center gap-2">
                      <Info className="w-4 h-4 text-blue-400" />
                      <span className="text-sm text-blue-300">Info alert - Additional information</span>
                    </div>
                  </div>

                  {/* Priority Indicators */}
                  <div className="space-y-3">
                    <Label className="text-sm text-slate-500">Priority Indicators (Border-left)</Label>
                    <div className={`p-3 rounded-lg border-l-4 border-red-500 ${theme === "dark" ? "bg-slate-800" : "bg-white border border-slate-200"}`}>
                      <p className="font-medium">Urgent Priority</p>
                      <p className="text-sm text-slate-400">Requires immediate attention</p>
                    </div>
                    <div className={`p-3 rounded-lg border-l-4 border-amber-500 ${theme === "dark" ? "bg-slate-800" : "bg-white border border-slate-200"}`}>
                      <p className="font-medium">High Priority</p>
                      <p className="text-sm text-slate-400">Time-sensitive task</p>
                    </div>
                    <div className={`p-3 rounded-lg border-l-4 border-blue-500 ${theme === "dark" ? "bg-slate-800" : "bg-white border border-slate-200"}`}>
                      <p className="font-medium">Normal Priority</p>
                      <p className="text-sm text-slate-400">Standard workflow item</p>
                    </div>
                    <div className={`p-3 rounded-lg border-l-4 border-slate-400 ${theme === "dark" ? "bg-slate-800" : "bg-white border border-slate-200"}`}>
                      <p className="font-medium">Low Priority</p>
                      <p className="text-sm text-slate-400">Can be addressed later</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Layout Tab */}
          <TabsContent value="layout" className="mt-6">
            <div className="space-y-6">
              <Card className={theme === "dark" ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200"}>
                <CardHeader>
                  <CardTitle className="text-lg">Spacing Scale</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {[1, 2, 3, 4, 6, 8, 12, 16].map((space) => (
                      <div key={space} className="flex items-center gap-4">
                        <span className="w-16 text-xs text-slate-500">space-{space}</span>
                        <div 
                          className="bg-teal-500 rounded"
                          style={{ width: `${space * 4}px`, height: "12px" }}
                        />
                        <span className="text-xs text-slate-400 font-mono">{space * 4}px / {space * 0.25}rem</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className={theme === "dark" ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200"}>
                <CardHeader>
                  <CardTitle className="text-lg">Border Radius</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-4">
                    {["sm", "md", "lg", "xl", "2xl", "3xl", "full"].map((size) => (
                      <div key={size} className="text-center">
                        <div 
                          className={`w-16 h-16 bg-teal-500 rounded-${size}`}
                          style={{ borderRadius: size === "full" ? "9999px" : undefined }}
                        />
                        <span className="text-xs text-slate-500 mt-1 block">{size}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className={theme === "dark" ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200"}>
                <CardHeader>
                  <CardTitle className="text-lg">Shadow Scale</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-6">
                    {["shadow-sm", "shadow-md", "shadow-lg", "shadow-xl", "shadow-2xl"].map((shadow) => (
                      <div key={shadow} className="text-center">
                        <div className={`w-20 h-20 bg-white rounded-lg ${shadow}`} />
                        <span className="text-xs text-slate-500 mt-2 block">{shadow}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
